import { StoreModelNames, StoreVaultCodeByModelName } from "../dali/type/dali.type";
import { StoreModel } from "../dali/vault-parser/parser.type";
import { type IModelView } from "../../models/view/model.view";
import { ModelValidator } from "@bitwarden/web-vault/app/models/store/model.validator";
import { DaliService } from "@bitwarden/web-vault/app/services/dali/dali.service";
import { inject } from "@angular/core";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { CollectionAbstraction } from "./collection.abstraction";
import { preserveNick } from "@bitwarden/web-vault/app/shared/utils/helper.account/account-nick.helper";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PartialVaultWriteError } from "@bitwarden/web-vault/app/services/dali/vault.writer";

export abstract class StoreModelCollection extends CollectionAbstraction {
  protected abstract type: StoreModelNames;
  protected abstract models: Map<string, StoreData>;
  protected validator: ModelValidator = new ModelValidator();
  protected daliService: DaliService = inject(DaliService);
  protected log: LogService = inject(LogService);

  abstract triggerChanges(): void;

  // Referential-integrity-on-delete, across collections. Another collection whose model holds an
  // id-list field pointing at rows in THIS collection (e.g. BudgetStoreModel.ctId -> categories)
  // registers a hook here; it runs when those rows are deleted so the now-dangling ids are stripped
  // from the dependent. This is the cross-collection generalisation of the intra-collection lTid
  // repair (#4442) — see referential-repair.ts (#4705) and #5565. The dependent's owning store
  // service registers the hook (see BudgetStoreService's constructor), so the wiring runs in the
  // non-circular direction and this collection never has to reference the dependent's collection.
  private referentialDependents: Array<(deletedIds: ReadonlySet<string>) => Promise<void>> = [];

  /**
   * Register a repair hook to run whenever rows are deleted from this collection. `hook` receives the
   * ids being deleted and is expected to strip any now-dangling reference to them from a dependent
   * collection (via `repairDanglingReferences`). Call once, at store init, from the dependent's
   * owning service.
   */
  registerReferentialDependent(hook: (deletedIds: ReadonlySet<string>) => Promise<void>): void {
    this.referentialDependents.push(hook);
  }

  /**
   * Run every registered dependent hook to strip the just-deleted ids, BEFORE this collection's own
   * delete write. Repair-first ordering means a later failure of the delete write leaves a dependent
   * merely missing a link to a still-present row (self-correcting, retryable) rather than the
   * dangling reference this repair exists to prevent. A hook rejection propagates and aborts the
   * delete before anything is removed locally — the safe outcome.
   */
  protected async repairReferentialDependents(deletedIds: ReadonlySet<string>): Promise<void> {
    for (const repair of this.referentialDependents) {
      await repair(deletedIds);
    }
  }

  /**
   * Load models into the in-memory map, frozen. `fromVaultLoad` marks a full (re)load of the vault
   * (initial bootstrap / reloadFromVault) as opposed to a write-back of models this collection just
   * saved: on a vault load an already-present id is a genuine duplicate worth flagging, whereas a
   * write-back re-setting an existing id is normal. The base ignores it (no per-load logging); a
   * subclass that does log duplicates (see TransactionStoreCollection) uses it to avoid firing on
   * every ordinary save/repair echo.
   */
  loadModels(models: StoreData[], _fromVaultLoad = false): void {
    models.forEach((model) => {
      if (this.validator.assertModel(this.type, model)) {
        this.models.set(model.id, this.deepFreeze(model));
      }
    });
  }

  /**
   * A partial vault write (#4908, and updates too as of #4735 round 4) still persisted some models
   * — with their new vid — before it failed overall. Load that committed subset into memory so a
   * retry updates them instead of re-creating duplicate ciphers, then let the caller surface the
   * failure. Returns whether anything was loaded, so the caller only triggers change signals when
   * the store actually changed. `protected` so cross-collection repair writes (e.g. the budget
   * `ctId` strip in BudgetStoreCollection, #5565) get the same partial-write recovery as save().
   */
  protected loadCommittedOnPartialWrite(error: unknown): boolean {
    if (!(error instanceof PartialVaultWriteError)) {
      return false;
    }
    this.loadModels(error.committed.get(StoreVaultCodeByModelName[this.type]) ?? []);
    return true;
  }

  /**
   * Rebuild a view array from the in-memory models, containing a per-model failure rather than
   * letting it abort the whole rebuild. If one model's view ctor throws — e.g. a malformed field the
   * {@link ModelValidator} didn't catch (#4313), such as a non-array `ctId` hitting `ctId.map(...)`
   * in `new BudgetView(model)` — that single row is logged by id and skipped, and every other row is
   * still built. Without this, one bad model makes `triggerChanges()` throw on *every* later call
   * (save, delete, link/unlink, store init), freezing the whole collection's view until that model is
   * removed (#5793). Each collection's `triggerChanges()` sets its signal from the result. A skipped
   * row is silently absent from the list — the log line is the only record, so it is a stack (not
   * just `Error: <message>`). `this.log` is console-only here; routing bad records to APM is a
   * separate concern (see #5694 for the transaction-collection APM work).
   */
  protected buildViews<M extends StoreData, V>(
    models: Map<string, M>,
    makeView: (model: M) => V,
  ): V[] {
    const views: V[] = [];
    models.forEach((model) => {
      try {
        views.push(this.deepFreeze(makeView(model)));
      } catch (error) {
        this.log.error(
          `Skipping malformed ${this.type} model ${model.id} in view rebuild: ` +
            (error instanceof Error ? (error.stack ?? error.message) : String(error)),
        );
      }
    });
    return views;
  }

  /**
   * Save views to vault and trigger the signal.
   * todo update for generic type function
   */
  async save(
    view: IModelView,
    organizationId?: string,
    collectionIds?: string[],
  ): Promise<StoreModel> {
    try {
      const updatedModel = await this.daliService.save(
        this.type,
        [view.toStoreModel()],
        organizationId,
        collectionIds,
      );
      this.loadModels(updatedModel);
      this.triggerChanges();
      return updatedModel.find((m) => view.id === m.id);
    } catch (error) {
      if (this.loadCommittedOnPartialWrite(error)) {
        this.triggerChanges();
      }
      throw error;
    }
  }

  // TODO: add a `share(view, organizationId, collectionId)` here mirroring `save()` but calling
  // `daliService.share`, so callers stop pulling `toStoreModel().vid` manually to add an
  // already-forked entity to another collection (see ManageAccountsComponent.addCollectionToExistingFork).

  /**
   * Delete the element from the vault and trigger the update signal.
   * The in-memory model is only removed once the vault write succeeds, so a failed
   * save leaves `models` (and thus `loadedCollection`) untouched and safe to retry.
   */
  async delete(view: IModelView): Promise<boolean> {
    const modelToDelete = this.models.get(view.id);
    if (!modelToDelete) {
      this.log.warning(`Try to delete ${view.id} but was never created.`);
      return false;
    }
    await this.repairReferentialDependents(new Set([view.id]));
    await this.daliService.save(this.type, [{ ...modelToDelete, del: true }]);
    this.models.delete(view.id);
    this.triggerChanges();
    return true;
  }

  /**
   * Delete the elements from the vault and trigger the update signal.
   * The in-memory models are only removed once the vault write succeeds, so a failed
   * save leaves `models` (and thus `loadedCollection`) untouched and safe to retry.
   *
   * Like `delete()`, each view is resolved against `this.models` and the stored model is the one
   * marked deleted: a view mutated after it was built (e.g. a clone with renamed fields) recomputes
   * a different id in `toStoreModel()`, so serialising it would target a row that doesn't exist and
   * leave the persisted one behind (#4668). A view with no stored model (sync paths may pass rows
   * not currently in the map) still falls back to its own serialised form.
   */
  async deleteFromVault(views: IModelView[]): Promise<boolean> {
    const modelsToDelete = views.map((view) => {
      const storedModel = this.models.get(view.id);
      return { ...(storedModel ?? view.toStoreModel()), del: true };
    });

    await this.repairReferentialDependents(new Set(views.map((view) => view.id)));
    await this.daliService.save(this.type, modelsToDelete);
    views.forEach((view) => this.models.delete(view.id));
    this.triggerChanges();
    return true;
  }

  /**
   * Save views to vault. Once the operation complete, the updated model will be loaded in the internal map.
   * **<span style="color: yellow;">Requires manual invocation of triggerChanges or automatic triggering through a dependency subscription </span>**
   */
  async saveToVault(
    views: IModelView[],
    triggerChanges = false,
    organizationId?: string,
    collectionIds?: string[],
  ): Promise<boolean> {
    const modelToSave = views.map((view) => view.toStoreModel());
    for (const model of modelToSave) {
      preserveNick(model as any, this.models.get(model.id) as any);
    }
    try {
      const updatedModel = await this.daliService.save(
        this.type,
        modelToSave,
        organizationId,
        collectionIds,
      );
      this.loadModels(updatedModel);
      if (triggerChanges) {
        this.triggerChanges();
      }
      return true;
    } catch (error) {
      if (this.loadCommittedOnPartialWrite(error) && triggerChanges) {
        this.triggerChanges();
      }
      throw error;
    }
  }
}

export type ModelCollectionInterface<T extends AllowedCollectionType> = {
  [n in keyof T]: StoreModelCollection;
};

type AllowedCollectionType =
  | UserStoreModelCollectionType
  | ImportStoreCollectionType
  | ReferenceDataStoreCollectionType
  | ScenarioStoreCollectionType
  | BudgetStoreCollectionType
  | SavingGoalStoreCollectionType;

export type UserStoreModelCollectionType = {
  accounts: StoreModelCollection;
  vaultFiles: StoreModelCollection;
  wizards: StoreModelCollection;
  categories: StoreModelCollection;
  classifications: StoreModelCollection;
  preferences: StoreModelCollection;
  institutions: StoreModelCollection;
  connectors: StoreModelCollection;
};

export type ImportStoreCollectionType = {
  sourceTransaction: StoreModelCollection;
  sourceBalance: StoreModelCollection;
  sourceCategory: StoreModelCollection;
  sourceAccount: StoreModelCollection;
  syncStatus: StoreModelCollection;
};

export type ReferenceDataStoreCollectionType = {
  referenceData: StoreModelCollection;
};

export type ScenarioStoreCollectionType = {
  scenario: StoreModelCollection;
  scenarioGroup: StoreModelCollection;
  estimate: StoreModelCollection;
  estimateAction: StoreModelCollection;
};

export type BudgetStoreCollectionType = {
  budget: StoreModelCollection;
};

export type SavingGoalStoreCollectionType = {
  savingGoal: StoreModelCollection;
};
